"use client";

import Image from "next/image";
import { Smartphone } from "lucide-react";
import { useTranslations } from "next-intl";
import type { AppStorePlatform, GetAppMoment } from "@award-party/shared";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";

interface GetAppSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  moment: GetAppMoment;
  /** The phone's store. Null on desktop, where both stores are offered. */
  store: AppStorePlatform | null;
  iosUrl: string | null;
  androidUrl: string | null;
  onStoreClick: (store: AppStorePlatform) => void;
}

const PRIMARY_BUTTON =
  "flex h-12 w-full cursor-pointer items-center justify-center rounded-2xl bg-(--mk-gold) text-[15px] font-bold text-(--mk-ink) no-underline transition-colors hover:bg-(--mk-gold-light)";

const STORE_BUTTON =
  "flex h-12 flex-1 cursor-pointer items-center justify-center gap-2 rounded-2xl border border-(--mk-cream-20) bg-(--mk-cream-surface) text-sm font-semibold text-(--mk-cream) no-underline transition-colors hover:border-(--mk-gold-border) hover:text-(--mk-gold)";

function AppIcon() {
  return (
    <Image
      src="/icon-192.png"
      alt=""
      width={64}
      height={64}
      className="h-16 w-16 rounded-[18px] shadow-[0_12px_32px_rgba(201,168,76,0.25)]"
    />
  );
}

export function GetAppSheet({
  open,
  onOpenChange,
  moment,
  store,
  iosUrl,
  androidUrl,
  onStoreClick,
}: GetAppSheetProps) {
  const t = useTranslations("GetApp");
  const title = moment === "picks_complete" ? t("picksTitle") : t("title");
  const body = moment === "picks_complete" ? t("picksBody") : t("body");

  if (store === null) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="rounded-3xl border-(--mk-cream-08) bg-(--mk-ink-soft) p-8 text-center sm:max-w-[420px]">
          <div className="flex justify-center">
            <AppIcon />
          </div>
          <DialogTitle className="font-serif text-2xl font-black leading-tight text-balance text-(--mk-cream)">
            {title}
          </DialogTitle>
          <DialogDescription className="text-sm leading-relaxed text-(--mk-cream-60)">
            {t("desktopBody")}
          </DialogDescription>
          <div className="mt-2 flex gap-3">
            {iosUrl ? (
              <a
                href={iosUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={STORE_BUTTON}
                onClick={() => onStoreClick("ios")}
              >
                <Smartphone className="h-4 w-4" aria-hidden />
                {t("appStore")}
              </a>
            ) : null}
            {androidUrl ? (
              <a
                href={androidUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={STORE_BUTTON}
                onClick={() => onStoreClick("android")}
              >
                <Smartphone className="h-4 w-4" aria-hidden />
                {t("googlePlay")}
              </a>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const storeUrl = store === "ios" ? iosUrl : androidUrl;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-[28px] border-(--mk-cream-08) px-6 pt-3 pb-[max(1.5rem,var(--safe-area-inset-bottom))]"
      >
        <div
          aria-hidden
          className="mx-auto mb-6 h-1 w-10 rounded-full bg-(--mk-cream-20)"
        />
        <div className="flex flex-col items-center text-center">
          <AppIcon />
          <SheetTitle className="mt-5 font-serif text-[26px] font-black leading-tight tracking-tight text-balance">
            {title}
          </SheetTitle>
          <SheetDescription className="mt-2 max-w-xs text-[15px] leading-relaxed">
            {body}
          </SheetDescription>
          <p className="mt-3 text-xs font-medium text-(--mk-cream-35)">
            {store === "ios" ? t("freeOnAppStore") : t("freeOnGooglePlay")}
          </p>
        </div>
        <div className="mt-6 flex flex-col gap-2">
          {storeUrl ? (
            <a
              href={storeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={PRIMARY_BUTTON}
              onClick={() => onStoreClick(store)}
            >
              {t("getTheApp")}
            </a>
          ) : null}
          <SheetClose className="flex h-12 w-full cursor-pointer items-center justify-center rounded-2xl text-sm font-medium text-(--mk-cream-60) transition-colors hover:text-(--mk-cream)">
            {t("continueInBrowser")}
          </SheetClose>
        </div>
      </SheetContent>
    </Sheet>
  );
}
